import {
    JSONCensusEquipmentsActionsConfirmation,
    ManualChange,
} from "../../data/models/JSONCensusEquipmentsActionsConfirmation";
import {
    ActionsEnum,
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons,
} from "../../data/models/CensusEnums";
import {
    JSONCensusSuggestedActionsAnalysisSummary,
    MappedSummary,
    Summary,
} from "../../data/models/JSONCensusSuggestedActionsAnalysisSummary";

export const mergeConfirmedActionsUtils = (
    action: JSONCensusEquipmentsActionsConfirmation,
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[],
    isMixedActionSelected?: boolean,
): JSONCensusEquipmentsActionsConfirmation[] => {
    const actionIndex = getActionIndexByStatusAndStatusDetailsUtils(
        action.status,
        action.statusDetail,
        action.statusReason,
        confirmedActions,
    );
    if (!action || !action.action) {
        return confirmedActions;
    }
    if (actionIndex !== -1) {
        const previousAction = confirmedActions[actionIndex];
        if (previousAction.action !== action.action || isMixedActionSelected) {
            action.manualChanges = [];
        }
        const newConfirmedActions = [...confirmedActions];
        newConfirmedActions[actionIndex] = action;
        return newConfirmedActions;
    } else {
        const newList = confirmedActions;
        newList.push(action);
        return newList;
    }
};

export const getActionIndexByStatusAndStatusDetailsUtils = (
    status: CensusEquipmentStatusesEnum,
    statusDetail: CensusEquipmentStatusDetailsEnum,
    statusReason: CensusEquipmentStatusReasons,
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[],
) => {
    return confirmedActions?.findIndex(
        (actionConfirmation) =>
            actionConfirmation.status === status &&
            actionConfirmation.statusDetail === statusDetail &&
            actionConfirmation.statusReason === statusReason,
    );
};

export const getActionByStatusAndStatusDetailAndReasonUtils = (
    status: CensusEquipmentStatusesEnum,
    statusDetail: CensusEquipmentStatusDetailsEnum,
    inventoryStatusReason: CensusEquipmentStatusReasons,
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[],
) => {
    return {
        ...confirmedActions?.find(
            (actionConfirmation) =>
                actionConfirmation.status === status &&
                actionConfirmation.statusDetail === statusDetail &&
                actionConfirmation.statusReason === inventoryStatusReason,
        )
    };
};

export const getActionsByStatusAndStatusDetailsUtils = (
    status: CensusEquipmentStatusesEnum,
    statusDetail: CensusEquipmentStatusDetailsEnum,
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[],
) => {
    return confirmedActions?.filter(
        (actionConfirmation) =>
            actionConfirmation.status === status &&
            actionConfirmation.statusDetail === statusDetail
    );
};

export const createAction = (
    status: CensusEquipmentStatusesEnum,
    statusDetail?: CensusEquipmentStatusDetailsEnum,
    statusReason?: CensusEquipmentStatusReasons,
    suggestedAction?: ActionsEnum,
) => {
    return {
        status,
        statusDetail,
        statusReason,
        action: suggestedAction,
        manualChanges: [],
    };
};

export const addOrRemoveAndMergeManualChangeUtils = (
    action: JSONCensusEquipmentsActionsConfirmation,
    newManualChange: ManualChange,
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[],
) => {
    const prevAction = {
        ...getActionByStatusAndStatusDetailAndReasonUtils(
            action.status,
            action.statusDetail,
            action.statusReason,
            confirmedActions,
        )
    };
    const prevManualChange = prevAction.manualChanges.find(
        (elem) => elem.entityGuid === newManualChange.entityGuid,
    );
    //No previous action or the there is no previous manual change and the new manual change is the same as the previous action
    if (
        !prevAction ||
        (!prevManualChange && newManualChange.action === prevAction.action)
    ) {
        //nothing to do
        return confirmedActions;
    }

    if (prevManualChange && prevAction.action === newManualChange.action) {
        // remove
        prevAction.manualChanges = prevAction.manualChanges.filter(
            (elem) => elem.entityGuid !== newManualChange.entityGuid,
        );
    } else if (prevManualChange) {
        // change
        prevAction.manualChanges = prevAction.manualChanges.map((elem) =>
            elem.entityGuid === prevManualChange.entityGuid ? newManualChange : elem,
        );
    } else {
        // add
        prevAction.manualChanges = [...prevAction.manualChanges, newManualChange]
    }
    return mergeConfirmedActionsUtils(prevAction, confirmedActions);
};

export const isMixedActionSelectedUtils = (
    action: JSONCensusEquipmentsActionsConfirmation,
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[],
) => {
    if (!action) {
        return false;
    }
    const actionIndex = getActionIndexByStatusAndStatusDetailsUtils(
        action.status,
        action.statusDetail,
        action?.statusReason,
        confirmedActions,
    );
    if (actionIndex !== -1) {
        return confirmedActions?.[actionIndex]?.manualChanges?.length > 0;
    }
    return false;
};

export const mapAndSortSummaryByStatusAndSuggestedAction = (suggestedActionsAnalysisSummary: JSONCensusSuggestedActionsAnalysisSummary): JSONCensusSuggestedActionsAnalysisSummary => {
    suggestedActionsAnalysisSummary.summary = suggestedActionsAnalysisSummary.summary
        .map((summary) => summary.availableActions.length > 0 && !summary.suggestedAction
            ? {...summary, suggestedAction: ActionsEnum.NO_ACTION}
            : summary)
        .sort(sortSummaryByStatusAndSuggestedAction);
    return suggestedActionsAnalysisSummary;
}

export const sortSummaryByStatusAndSuggestedAction = (
    firstSummary: Summary,
    secondSummary: Summary,
) => {
    const firstStatus = firstSummary?.statusAndDetailCount?.inventoryStatus;
    const secondStatus = secondSummary?.statusAndDetailCount?.inventoryStatus;
    const firstStatusDetail = firstSummary?.statusAndDetailCount?.inventoryStatusDetail;
    const secondStatusDetail = secondSummary?.statusAndDetailCount?.inventoryStatusDetail;
    const firstReason = firstSummary?.statusAndDetailCount?.inventoryStatusReason;
    const secondReason = secondSummary?.statusAndDetailCount?.inventoryStatusReason;
    const firstAvailableActions = firstSummary?.availableActions?.length > 1;
    const secondAvailableActions = secondSummary?.availableActions?.length > 1;

    if (firstStatus === CensusEquipmentStatusesEnum.FAIL && secondStatus !== CensusEquipmentStatusesEnum.FAIL) {
        return -1;
    }
    if (firstStatus !== CensusEquipmentStatusesEnum.FAIL && secondStatus === CensusEquipmentStatusesEnum.FAIL) {
        return 1;
    }
    if (firstStatusDetail && !firstStatusDetail) {
        return -1;
    }
    if (!firstStatusDetail && secondStatusDetail) {
        return 1;
    }
    if (firstReason && !secondReason) {
        return -1;
    }
    if (!firstReason && secondReason) {
        return 1;
    }
    if (firstAvailableActions > secondAvailableActions) {
        return -1;
    }
    if (firstAvailableActions < secondAvailableActions) {
        return 1;
    }
    return 0;
};

export const createConfirmedActions = (
    suggestedActionsAnalysisSummary: MappedSummary[],
): JSONCensusEquipmentsActionsConfirmation[] => {
    const defaultActions = [];
    suggestedActionsAnalysisSummary
        ?.filter(
            (summary) => summary?.suggestedAction || summary?.withReason?.some((reason) => reason?.suggestedAction))
        ?.forEach((summary) => {
            if (summary?.withReason?.length > 0) {
                summary?.withReason?.forEach((reason) => {
                    const newAction = createAction(
                        summary?.inventoryStatus,
                        summary?.inventoryStatusDetail,
                        reason?.inventoryStatusReason ?? CensusEquipmentStatusReasons.OTHER,
                        reason?.suggestedAction,
                    );
                    defaultActions.push(newAction);
                })
            } else {
                const newAction = createAction(
                    summary?.inventoryStatus,
                    summary?.inventoryStatusDetail,
                    summary?.inventoryStatusReason,
                    summary?.suggestedAction,
                );
                defaultActions.push(newAction);
            }
        });
    return defaultActions;
};

